import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import {
  ButtonComponent,
  IconComponent,
  InputComponent,
  PasswordComponent,
} from '@app-ui';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService, BaseComponent, CustomToasterService } from '@app-utils';
import { finalize, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'auth-change-password',
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.less',
  standalone: true,
  imports: [
    AsyncPipe,
    ButtonComponent,
    IconComponent,
    InputComponent,
    ReactiveFormsModule,
    RouterLink,
    PasswordComponent,
    NgForOf,
    NgIf,
    NgClass,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordComponent extends BaseComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _fb = inject(FormBuilder);
  private readonly _apiSvc = inject(ApiService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _translate = inject(TranslateService);
  private readonly _customToaster = inject(CustomToasterService);
  private readonly _msg = this._translate.instant(
    'auth.component.password-card.message.validation',
    { value: 2 },
  );

  strength = 0;
  accessToken = '';

  fg = this._fb.group(
    {
      newPassword: [
        null,
        [
          Validators.required,
          (control: FormControl) => this.newPasswordValidator(control),
        ],
      ],
      confirmPassword: [null, [Validators.required]],
    },
    {
      validator: this.confirmPasswordValidator(
        'newPassword',
        'confirmPassword',
      ),
    },
  );

  @Input() email = '';

  /**
   * validator
   *
   * @param control
   */
  newPasswordValidator(control: FormControl) {
    const value = typeof control.value === 'string' ? control.value : '';

    this.strength =
      (value.length > 6 ? 1 : 0) +
      (value.length > 10 ? 1 : 0) +
      (/[A-Z]/.test(value) ? 1 : 0) +
      (/[0-9]/.test(value) ? 1 : 0) +
      (/[\W]/.test(value) ? 1 : 0);

    return this.strength <= 2 ? { strength: { message: this._msg } } : null;
  }

  /**
   * Confirm password validator
   *
   * @param newPwdControlName
   * @param confirmPwdControlName
   */
  confirmPasswordValidator(
    newPwdControlName: string,
    confirmPwdControlName: string,
  ): ValidatorFn {
    return (formGroup: AbstractControl) => {
      const newPwdControl = formGroup.get(newPwdControlName);
      const confirmPwdControl = formGroup.get(confirmPwdControlName);

      if (!newPwdControl || !confirmPwdControl) {
        return null;
      }

      return newPwdControl.value === confirmPwdControl.value
        ? null
        : { passwordMismatch: true };
    };
  }

  ngOnInit(): void {
    this._activatedRoute.queryParams.subscribe((params) => {
      this.accessToken = params['accesstoken'];
      if (!this.accessToken) {
        this._customToaster.error(
          this._translate.instant(
            'auth.component.password-card.message.link-expired',
          ),
          'Error',
        );
      }
    });
  }

  changePassword() {
    this.loading$.next(true);
    const { newPassword } = this.fg.value;
    this._apiSvc
      .resetPassword(this.accessToken, newPassword)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this.loading$.next(false)),
      )
      .subscribe(() => {
        this._customToaster.success(
          this._translate.instant(
            'auth.component.password-card.message.reset-success',
          ),
        );
        void this._router.navigate(['/auth']);
      });
  }
}
